import { getAllPoolItems, getAllPools, getFirmPools, getPool, getPoolChangeLog, getPoolItems } from './http/labPools';
import type { GetPoolChangeLogOptions, GetPoolItemsOptions } from './http/labPools';
import type { QueryObserverOptions } from '@tanstack/query-core';
import type { GetPoolChangeLogResponse, ListPoolItemsResponse } from '@thinkalpha/locates-ws-client';
import type { GetPoolResponse, ListPoolsResponse } from '@thinkalpha/ui-types/types/admin/lab-pools';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Read-only query factories and invalidation helpers for LAB
 * inventory pools. Kept separate from {@link ../labPoolsMutations}
 * so read-only consumers do not transitively pull in the write
 * code paths.
 */

export const LAB_POOLS = 'lab-pools';
export const LAB_POOL = 'lab-pool';
export const LAB_POOL_ITEMS = 'lab-pool-items';
export const LAB_POOL_CHANGELOG = 'lab-pool-changelog';

/**
 * 30s `staleTime` for both the pool list and per-pool items. Pool
 * membership and inventory both change at most every few seconds via
 * the locates-ws apply pipeline; without a `staleTime`, every focus
 * event or remount triggers a refetch and the table flickers.
 * Mutations explicitly call `invalidatePoolsQuery` /
 * `invalidatePoolItemsQuery`, so a short stale window is safe.
 */
const STALE_TIME_MS = 30_000;

/**
 * Lists all pools the caller has at least `reader` access to. The
 * backend gates the result-set on `effectiveAccess`; superuser callers
 * see every pool, firm-scoped callers only theirs. Use
 * {@link getFirmPoolsQuery} when you already know the target firm.
 */
export const getAllPoolsQuery = (): QueryObserverOptions<ListPoolsResponse, Error, ListPoolsResponse> => {
    return {
        queryKey: [LAB_POOLS],
        queryFn: () => getAllPools(),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * Lists pools scoped to a single firm. Accepts `undefined` to
 * support React-Query's standard "conditional fetch" pattern; the
 * query stays disabled until a firmId is provided.
 */
export const getFirmPoolsQuery = (
    firmId: string | undefined,
): QueryObserverOptions<ListPoolsResponse, Error, ListPoolsResponse> => {
    return {
        queryKey: [LAB_POOLS, firmId],
        queryFn: () => getFirmPools(firmId!),
        enabled: Boolean(firmId),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * Inspect a single pool by id. Useful when the caller has a poolId
 * (e.g. from a deep link) but no list result to source the metadata
 * from.
 */
export const getPoolQuery = (
    poolId: string | undefined,
): QueryObserverOptions<GetPoolResponse, Error, GetPoolResponse> => {
    return {
        queryKey: [LAB_POOL, poolId],
        queryFn: () => getPool(poolId!),
        enabled: Boolean(poolId),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * Lists inventory items for a single pool. Page params (`skip`,
 * `limit`) and the optional exact-match `symbol` filter are all part
 * of the cache key so distinct page/filter slices don't clobber each
 * other and the per-page fetches can be invalidated together via the
 * `[LAB_POOL_ITEMS, poolId]` prefix.
 */
export const getPoolItemsQuery = (
    poolId: string | undefined,
    options: GetPoolItemsOptions = {},
): QueryObserverOptions<ListPoolItemsResponse, Error, ListPoolItemsResponse> => {
    const { symbol, skip, limit } = options;
    return {
        queryKey: [LAB_POOL_ITEMS, poolId, symbol ?? null, skip ?? null, limit ?? null],
        queryFn: () => getPoolItems(poolId!, { symbol, skip, limit }),
        enabled: Boolean(poolId),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * Loads a pool's entire inventory via real backend pagination. Backs
 * the "all rows" table mode and the CSV export; both share this cache
 * entry. The key is distinct from the paged / symbol-filtered slices
 * but still under the `[LAB_POOL_ITEMS, poolId]` prefix so it is
 * invalidated alongside them.
 */
export const getAllPoolItemsQuery = (
    poolId: string | undefined,
): QueryObserverOptions<ListPoolItemsResponse, Error, ListPoolItemsResponse> => {
    return {
        queryKey: [LAB_POOL_ITEMS, poolId, 'all'],
        queryFn: () => getAllPoolItems(poolId!),
        enabled: Boolean(poolId),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * Pages a pool's audit-trail entries. `skip`/`limit` participate in
 * the cache key so distinct pages don't clobber each other; the
 * `[LAB_POOL_CHANGELOG, poolId]` prefix invalidates all pages at once.
 */
export const getPoolChangeLogQuery = (
    poolId: string | undefined,
    options: GetPoolChangeLogOptions = {},
): QueryObserverOptions<GetPoolChangeLogResponse, Error, GetPoolChangeLogResponse> => {
    const { skip, limit } = options;
    return {
        queryKey: [LAB_POOL_CHANGELOG, poolId, skip ?? null, limit ?? null],
        queryFn: () => getPoolChangeLog(poolId!, { skip, limit }),
        enabled: Boolean(poolId),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * Invalidate every cached page of a pool's change-log, or the whole
 * `LAB_POOL_CHANGELOG` namespace when no poolId is provided.
 */
export const invalidatePoolChangeLogQuery = (
    queryClient: ReactBindings['QueryClient'],
    poolId: string | undefined = undefined,
) => {
    return poolId
        ? queryClient.invalidateQueries({ queryKey: [LAB_POOL_CHANGELOG, poolId] })
        : queryClient.invalidateQueries({ queryKey: [LAB_POOL_CHANGELOG] });
};

/**
 * Invalidate the pool-list query. Pass a firmId to scope the
 * invalidation; omit to clear all pool-list cache entries.
 */
export const invalidatePoolsQuery = (
    queryClient: ReactBindings['QueryClient'],
    firmId: string | undefined = undefined,
) => {
    return firmId
        ? queryClient.invalidateQueries({ queryKey: [LAB_POOLS, firmId] })
        : queryClient.invalidateQueries({ queryKey: [LAB_POOLS] });
};

/**
 * Invalidate one pool's items, or all item caches if no poolId.
 * The match is prefix-based so both unfiltered and symbol-filtered
 * caches for a given pool are invalidated together.
 */
export const invalidatePoolItemsQuery = (
    queryClient: ReactBindings['QueryClient'],
    poolId: string | undefined = undefined,
) => {
    return poolId
        ? queryClient.invalidateQueries({ queryKey: [LAB_POOL_ITEMS, poolId] })
        : queryClient.invalidateQueries({ queryKey: [LAB_POOL_ITEMS] });
};

/**
 * Invalidate the single-pool inspect query, or all single-pool
 * cache entries if no poolId.
 */
export const invalidatePoolQuery = (
    queryClient: ReactBindings['QueryClient'],
    poolId: string | undefined = undefined,
) => {
    return poolId
        ? queryClient.invalidateQueries({ queryKey: [LAB_POOL, poolId] })
        : queryClient.invalidateQueries({ queryKey: [LAB_POOL] });
};
